/**
 * Created by jordid on 21/05/15.
 *  @module contact
 */

import { v4 as uuidv4 } from 'uuid';

import { isMobile, isMobileOrTablet } from './device';
import { bindAjaxForm } from './form.es6';
import LocalStorage from './localStorage';
import MAEvent from './ma-event';
import Navigation from './navigation';
import Realytics from './realytics.es6';
import { bindForm } from './tagging';
import {
    EstateType,
    eventTypeWithEstateType,
    thorAvItems,
    thorDistributionType,
    thorPushEvent,
} from './thor';
import * as Utils from './utils';
import WA from './wa';
/**
 * Map the datas from context to be send to thor
 *
 * @param {String} eventName event name without prefix page
 * @param {String} zone
 * @returns
 */

const dimensionsRealtorContactForm = {
    'REALTOR_MESSAGE_PROJECTS.SELLER': 'WantstoSell',
    'REALTOR_MESSAGE_PROJECTS.SELLER_BUYER': 'WantstoSellAndBuy',
    'REALTOR_MESSAGE_PROJECTS.BUYER': 'WantsToBuy',
    'REALTOR_MESSAGE_PROJECTS.TENANT': 'WantsToRent',
    'REALTOR_MESSAGE_PROJECTS.LESSOR': 'WantstoFindTenant',
    'REALTOR_MESSAGE_PROJECTS.MANAGER': 'ManagehisProperty',
    'REALTOR_MESSAGE_PROJECTS.OTHER': 'other_project',
};

/**
 * Checks if the current page is a realtor-related page.
 *
 * This function examines the `pathname` of the current `window.location`
 * and determines if it matches any of the predefined paths for realtor-related pages.
 *
 * @returns {boolean}
 */
function isRealtorPage() {
    const { pathname } = window.location;
    return (
        pathname.includes('/agence-immobiliere/') ||
        pathname.includes('/professionnel-immobilier/') ||
        pathname.includes('/mandataire-immobilier/')
    );
}

/**
 * Checks if the current page is a price map page.
 *
 * This function examines the `pathname` of the current `window.location`
 * and determines if it includes the specific path for price map pages.
 *
 * @returns {boolean}
 */
function isPriceMapPage() {
    const { pathname } = window.location;
    return pathname.includes('/prix-immobilier/');
}

/**
 * Checks if the current page is a sold property page.
 *
 * This function examines the `pathname` of the current `window.location`
 * and determines if it includes the specific path for sold property pages.
 *
 * @returns {boolean}
 */
function isSoldPropertyPage() {
    const { pathname } = window.location;
    return pathname.includes('/biens-vendus/');
}

/**
 * Sends tracking event (new data 2025 standard)
 *
 *
 * @param {String} eventName - The name of the event to be sent.
 * @param {String} pageTitle - The title of the page where the event occurred.
 * @param {String} realtorId - The unique identifier of the realtor.
 */
function sendFormTrackingEvent(eventName, pageTitle, realtorId) {
    thorPushEvent(eventName, {
        av_category: 'find_pro',
        av_pagetitle: pageTitle,
        av_items: [
            {
                client_id: realtorId,
                country: 'FRA',
                id: realtorId, // it's duplicated but asked by tracking team
                name: 'agency',
                publication_id: 'MA',
            },
        ],
    });
}

/**
 * Retrive a leadId depending on a realtor
 * @param {String} realtorId
 * @returns String
 */
export function getLeadId(realtorId) {
    let leadId = LocalStorage.getItem(`lead_id_${realtorId}`);
    if (leadId === null) {
        LocalStorage.setItem(`lead_id_${realtorId}`, uuidv4());
        leadId = LocalStorage.getItem(`lead_id_${realtorId}`);
    }
    return leadId;
}

export const contactMapThorEvent = params => {
    const { pathname } = window.location;
    const { eventName, zone, project, realtorId, leadId } = params;
    if (pathname.includes('/annonces/')) {
        const { transactionType, listing, listings } = MA.Context;
        const eventArgs = {
            av_category: 'classified',
            av_distribution_type_global: thorDistributionType(transactionType),
            av_estate_type_global: EstateType[listing.item_type.key],
            av_items: thorAvItems({
                listName: 'classified_detail',
                listing,
                listings,
                name: 'classified',
                transactionType,
            }),
            av_zone: zone,
        };
        const eventType = eventTypeWithEstateType(`lead_classified_${eventName}`, transactionType);
        thorPushEvent(eventType, eventArgs);
    }

    if (isRealtorPage()) {
        const eventArgs = {
            av_category: 'find_pro',
            av_items: [
                {
                    id: realtorId,
                    lead_id: leadId,
                },
            ],
            av_user: {
                project: dimensionsRealtorContactForm[project],
            },
        };

        thorPushEvent(`lead_agency_${eventName}`, eventArgs);
    }
};

/**
 * Display the popin with the realtor contact for and past sale details
 * @param {jQuery} $html Popin html content
 * @param {String} uaEvent
 * @param {String} waSuccessEvent
 * @param {String} waLandedEvent
 */
export function referenceMessage($html, uaEvent, waSuccessEvent, waLandedEvent) {
    // On desktop, show the phone number
    if (!isMobile()) {
        $html.find('[data-phone-number]').each(function phoneNumberEach() {
            const $this = $(this);

            let phoneIsShown = false;
            $this.on('click', event => {
                event.preventDefault(); // Do launch call
                if (!phoneIsShown) {
                    $this.find('[data-phone-placeholder]').html($this.data('phone-call'));
                    phoneIsShown = true;
                }
            });
        });
    }

    function initGallery() {
        const scrollDuration = 250;
        const buttonClass = 'reference-popin__gallery-button';
        let scrollPending = false;

        function onGalleryButtonClick() {
            const horizontalOffset = 20;
            const $gallery = $html.find('[data-reference-gallery]');
            const $button = $(this);
            const isNext = $button.hasClass(`${buttonClass}--next`);
            const scrollLeft = $gallery.scrollLeft();
            const canScrollToLeft = scrollLeft > 0;
            const canScrollToRight = scrollLeft < $gallery[0].scrollWidth - $gallery.innerWidth();

            if (scrollPending || (isNext && !canScrollToRight) || (!isNext && !canScrollToLeft)) {
                return;
            }

            function scrollGalleryTo(scrollTarget) {
                scrollPending = true;
                $gallery.animate({ scrollLeft: scrollTarget }, scrollDuration, () => {
                    scrollPending = false;
                });
            }

            function scrollGalleryToPicture($picture) {
                if ($picture.length > 0) {
                    const leftOffset =
                        Math.ceil($picture.position().left) +
                        parseInt($picture.css('margin-left'), 10);
                    scrollGalleryTo(`+=${leftOffset}`);
                }
            }

            $gallery.children().each(function childrenGalleryEach() {
                const $picture = $(this);
                if ($picture.position().left > (isNext ? 1 : -1) * horizontalOffset) {
                    scrollGalleryToPicture(isNext ? $picture : $picture.prev());
                    return false;
                }

                return undefined;
            });
        }

        $html.on('click', `.${buttonClass}`, onGalleryButtonClick);
    }

    function initForm() {
        const $form = $html.find('[data-message-form]');
        const realtorId = String($form.data('realtor-id'));
        const realtorAtype = $form.data('realtor-type');
        const $messageSuccess = $html.find('[data-message-success]');
        const trackingPageTitle = isRealtorPage() ? 'agency_detail' : 'price_map';
        const trackingPageTitleSubmit = isSoldPropertyPage()
            ? 'sold_property_agency_detail'
            : trackingPageTitle;
        const needNewTrackingFormat = isRealtorPage() || isPriceMapPage() || isSoldPropertyPage();
        if (needNewTrackingFormat) {
            sendFormTrackingEvent('lead_agency_form_open', trackingPageTitle, realtorId);
        }

        $form.one(MAEvent.formSubmitSuccess, () => {
            if (needNewTrackingFormat) {
                sendFormTrackingEvent(
                    'lead_agency_form_submit',
                    trackingPageTitleSubmit,
                    realtorId,
                );
            }
            Realytics.trackDDE();
            window.scrollTo(0, $messageSuccess.offset().top - 20);
        });
        bindForm($form, uaEvent, waSuccessEvent, waLandedEvent, null, {
            id: realtorId,
            atype: realtorAtype,
        });
        bindAjaxForm($form, $messageSuccess, true, null);
    }

    /**
     * Function to open the realtor page of a clicked realtor list item
     * @param  {String} dataAttributeContainer Data attribute used to scope the realtor list item
     */
    function initRealtorListItem(dataAttributeContainer) {
        const containerElement = dataAttributeContainer || document;
        const realtor = containerElement.querySelector('[data-reference-realtor]');
        const realtorHref = realtor.getAttribute('data-reference-realtor-href');

        const openRealtorPage = event => {
            event.preventDefault();
            let targetUrl = realtorHref;
            if (isSoldPropertyPage()) {
                const url = new URL(realtorHref, window.location.origin);
                url.searchParams.set('m', 'sold_property_agency_detail');
                targetUrl = url.toString();
            }
            window.open(targetUrl, '_blank');
        };

        realtor.onclick = openRealtorPage;

        // Also handle click on the title link
        const titleLink = containerElement.querySelector('[data-realtor-list-item-title]');
        if (titleLink) {
            titleLink.onclick = openRealtorPage;
        }
    }

    initGallery();
    initForm();
    initRealtorListItem(document.querySelector('[data-message-modal-content]'));
}

/**
 *
 * @param {jQuery} $html Popin container
 * @param uaEvent
 * @param waSuccessEvent
 * @param waLandedEvent
 */
export function realtorMessage($html, uaEvent, waSuccessEvent, waLandedEvent) {
    const $form = $html.find('[data-message-form]');
    const $messageSuccess = $html.find('[data-message-success]');
    const atype = $form.data('realtor-type');

    $('#optin').on('change', function optionChange() {
        if (this.checked) {
            $('input[data-intent]').val('REALTOR_MESSAGE_INTENTS.DIRECT_EVALUATION_REQUEST');
        } else {
            $('input[data-intent]').val('REALTOR_MESSAGE_INTENTS.REALTOR_CONTACT');
        }
    });

    $form.one(MAEvent.formSubmitAttempt, () => {
        const project = $form.find('#project')[0];
        contactMapThorEvent({
            eventName: 'form_submit',
            zone: 'side|sticky',
            project: project?.value,
            realtorId: $form.data('realtor-id'),
            leadId: getLeadId($form.data('realtor-id')),
        });
    });

    $form.one(MAEvent.formSubmitSuccess, () => {
        if (
            $('input[data-intent]')[0].value === 'REALTOR_MESSAGE_INTENTS.DIRECT_EVALUATION_REQUEST'
        ) {
            Realytics.trackDDE();
        }
        const project = $form.find('#project')[0];
        contactMapThorEvent({
            eventName: 'form_confirm',
            zone: 'side|sticky',
            project: project?.value,
            realtorId: $form.data('realtor-id'),
            leadId: getLeadId($form.data('realtor-id')),
        });
        window.scrollTo(0, $messageSuccess.offset().top - 20);
    });

    bindForm($form, uaEvent, waSuccessEvent, waLandedEvent, null, {
        id: window.MA.Context.realtorId,
        atype,
    });
    bindAjaxForm($form, $messageSuccess, true, null);
}

export function evaluationFormReady() {
    const $form = $('[data-evaluation-form]');
    const $field = $('[data-place-zip]');
    const $dropdown = $('[data-place-name]');

    $form[0].reset();

    function zipChangeHandler() {
        const value = $(this).val();
        if (!value || value.length !== 5) {
            return;
        }

        $.getJSON($field.data('url'), {
            q: value,
            types: 'cities,arrmuns,subcities',
        }).done(data => {
            $dropdown.html(
                data.response.places.map(place => {
                    const cityId = Utils.getURLParam('item_city_id');
                    if (cityId === place.id) {
                        return `<option value=${place.id} selected>${place.name_short}</option>`;
                    }

                    return `<option value=${place.id}>${place.name_short}</option>`;
                }),
            );
        });
    }

    $field.on('textchange', zipChangeHandler);
    zipChangeHandler.call($field);

    bindForm($form, {
        category: 'Demande evaluation',
        actionPrefix: 'evaluation_request:form',
        label: 'evaluation_request:form',
    });
    bindAjaxForm($form);
}

/**
 * Handle realtor form bindings and submission
 * @param  {String} gaCategory Google Analytics Category
 * @param  {String} waSource WA Source
 */
export function initializeRealtorContactForm(gaCategory, gaActionPrefix, waSource, formContainer) {
    const $formContainer = formContainer;
    const $form = $formContainer.find('[data-message-form]');

    const $waData = `source=${WA.updateSourceWhenRetargeting(waSource)}|realtor_id=${$form.data(
        'realtor-id',
    )}`;

    realtorMessage(
        $formContainer,
        {
            category: gaCategory,
            actionPrefix: gaActionPrefix,
            label: $formContainer.find('[name="ga_tag"]').val(),
        },
        {
            key: 'form_success',
            name: 'realtor_contact',
            data: $waData,
        },
    );
}

/**
 * Show a realtor message form in a popin
 * * Only on Mobile
 */
export function initializeRealtorMessagePopin(gaCategory, waSource) {
    if (!isMobile()) {
        return;
    }

    $('[data-realtor-message-button]').on('click', function onRealtorMessageClick(e) {
        const $this = $(this);
        e.preventDefault();
        contactMapThorEvent({ eventName: 'form_opened', zone: 'bottom|sticky' });
        $.magnificPopup.open(
            {
                type: 'ajax',
                fixedContentPos: 'fixed',
                items: {
                    src: $this.data('url'),
                },
                callbacks: {
                    ajaxContentAdded() {
                        // Ajax content is loaded and appended to DOM
                        Navigation.disablePageScroll();
                        const $form = $(this.content).find('[data-message-form]');

                        realtorMessage(
                            this.content,
                            {
                                category: gaCategory,
                                actionPrefix: 'realtor:message:popin',
                                label: `from:${$this.data('ua-event-label')}`,
                            },
                            {
                                key: 'form_success',
                                name: 'realtor_contact',
                                data: `source=${WA.updateSourceWhenRetargeting(
                                    waSource,
                                )}|realtor_id=${$form.data('realtor-id')}`,
                            },
                        );
                    },
                    close() {
                        Navigation.enablePageScroll();
                    },
                },
            },
            0,
        );
    });
}

/**
 * Keep the contact form on top of the viewport when the user scroll down the page
 * ⚠️ Only on Desktop
 * @param  {Number} offsetTop Sticky Kit offset top option
 */
export function initializeStickyContactForm(offsetTop) {
    if (isMobileOrTablet()) {
        return;
    }

    $('[data-contact-form-sticky]').stick_in_parent({
        spacer: false,
        offset_top: offsetTop,
        sticky_class: 'contact-form-sticky',
    });
}

/**
 * Show realtor phone number after clicking on a button
 * ⚠️ Only on Desktop
 */
export function initializeRealtorPhoneButton() {
    const $showPhoneButton = $('[data-realtor-show-phone-button]');

    $showPhoneButton.on('click', e => {
        contactMapThorEvent({ eventName: 'phone_display', zone: e.currentTarget.dataset.avZone });

        e.currentTarget.classList.add('hide');
        e.currentTarget.nextElementSibling.classList.remove('hide');
    });
}

export default {
    referenceMessage,
    realtorMessage,
    evaluationFormReady,
};
